import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useConfig } from '@/hooks/useConfig'
import useTouchScrollLock from '@/hooks/useTouchScrollLock'

import './LinePicker.scss'

export type LinePickerOption = { value: string; label: string }

export interface LinePickerProps {
    /** Label of the field, also the title of the sheet */
    label: string
    placeholder: string
    options: LinePickerOption[]
    onSelect: (value: string) => void
    disabled?: boolean
}

// the part of a label that matches the search is bold
const highlight = (label: string, needle: string) => {
    const at = needle ? label.toLowerCase().indexOf(needle) : -1
    if (at < 0) return label
    return (
        <>
            {label.slice(0, at)}
            <mark className="line-picker-sheet__match">{label.slice(at, at + needle.length)}</mark>
            {label.slice(at + needle.length)}
        </>
    )
}

// A phone picks a line in a sheet that fills the screen: a search field above the list, and nothing positioned next to
// the field, so the virtual keyboard has nothing to cover (the height of the sheet follows it, see useVirtualKeyboard)
const LinePicker = ({ label, placeholder, options, onSelect, disabled = false }: LinePickerProps) => {
    const { t } = useTranslation('general')
    // the sheet is in document.body, outside .app, which carries the dark mode class
    const { isDarkTheme } = useConfig()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const triggerRef = useRef<HTMLButtonElement>(null)
    const sheetRef = useRef<HTMLDivElement>(null)
    const titleId = useId()

    useTouchScrollLock(open, '.line-picker-sheet')

    const needle = query.trim().toLowerCase()
    const filtered = useMemo(
        () => (needle ? options.filter(option => option.label.toLowerCase().includes(needle)) : options),
        [options, needle]
    )

    const close = useCallback(() => {
        setOpen(false)
        setQuery('')
    }, [])

    useEffect(() => {
        if (!open) return
        const trigger = triggerRef.current
        sheetRef.current?.focus()
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close()
        }
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('keydown', onKeyDown)
            trigger?.focus()
        }
    }, [open, close])

    return (
        <>
            <div className="fms__control-wrapper line-picker__field">
                <button
                    ref={triggerRef}
                    type="button"
                    className="fms__control fms__control--with-label line-picker__trigger"
                    disabled={disabled}
                    aria-haspopup="dialog"
                    onClick={() => setOpen(true)}
                >
                    <span className="fms__field-label">{label}</span>
                    <span className="fms__placeholder">{placeholder}</span>
                </button>
                <div className="fms__custom-indicator">
                    <i className="a-icon boschicon-bosch-ic-down fms__arrow-icon" aria-hidden="true" />
                </div>
            </div>

            {open &&
                createPortal(
                    <div
                        ref={sheetRef}
                        className={`line-picker-sheet ${isDarkTheme ? '-dark-mode' : ''}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        tabIndex={-1}
                    >
                        <div className="line-picker-sheet__header">
                            <h2 id={titleId} className="line-picker-sheet__title">
                                {label}
                            </h2>
                            <button
                                type="button"
                                className="a-button a-button--integrated -without-label line-picker-sheet__close"
                                aria-label={t('general:settings:linePickerClose')}
                                onClick={close}
                            >
                                <i className="a-icon a-button__icon boschicon-bosch-ic-close" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="line-picker-sheet__search-wrapper">
                            <i className="a-icon boschicon-bosch-ic-search line-picker-sheet__search-icon" aria-hidden="true" />
                            <input
                                type="search"
                                className="line-picker-sheet__search"
                                value={query}
                                placeholder={t('general:settings:linePickerSearch')}
                                aria-label={t('general:settings:linePickerSearch')}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                enterKeyHint="search"
                                onChange={e => setQuery(e.target.value)}
                            />
                        </div>
                        <ul className="line-picker-sheet__list" role="listbox" aria-label={label}>
                            {filtered.map(option => (
                                <li key={option.value} role="presentation">
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={false}
                                        className="line-picker-sheet__option"
                                        onClick={() => {
                                            onSelect(option.value)
                                            close()
                                        }}
                                    >
                                        {highlight(option.label, needle)}
                                    </button>
                                </li>
                            ))}
                            {filtered.length === 0 && (
                                <li className="line-picker-sheet__empty" role="presentation">
                                    {t('general:settings:linePickerEmpty')}
                                </li>
                            )}
                        </ul>
                    </div>,
                    document.body
                )}
        </>
    )
}

export default LinePicker
